/**
 * specific-variant-data.js
 *
 * Fetches variant metafield custom.variant_product_data for the currently selected variant
 * and writes it into a target DOM element as PLAIN TEXT (rich text JSON -> text).
 *
 * Liquid target (one of these must exist):
 *   <div id="specific-variant-output"></div>
 *   or
 *   <div data-specific-variant-output></div>
 *
 * Optional (if you want the JSON stored as an attribute too):
 *   <div data-specific-variant></div>
 */

(() => {
  const CFG = {
    apiVersion: '2024-04',
    storefrontToken: 'a5d7e5877a97b343a85b6b4871fe1b02',
    metafield: { namespace: 'custom', key: 'variant_product_data' },

    outputSelector: '[data-specific-variant-output]',
    outputIdFallback: 'specific-variant-output',

    attrSelector: '[data-specific-variant]',
    attrName: 'data-specific-variant',

    // If your theme dispatches variant change events, list them here (common ones)
    variantChangeEvents: ['variant:change', 'variant:changed', 'product:variant-change']
  };

  const state = {
    lastVariantId: null,
    cache: new Map() // variantId -> { raw, parsed }
  };

  document.addEventListener('DOMContentLoaded', () => {
    const initialVariantId = getCurrentVariantId();
    if (initialVariantId) {
      state.lastVariantId = initialVariantId;
      fetchAndRender(initialVariantId);
    }

    // 1) Try listening to theme events
    CFG.variantChangeEvents.forEach(evt => {
      document.addEventListener(evt, (e) => {
        const id = e?.detail?.variant?.id || getCurrentVariantId();
        if (!id || id === state.lastVariantId) return;
        state.lastVariantId = id;
        fetchAndRender(id);
      });
    });

    // 2) Fallback: observe the hidden variant input value changing
    observeVariantInput();
  });

  function getCurrentVariantId() {
    const el = document.querySelector('.product-form form input[name="id"]');
    const v = el?.value;
    return v && /^\d+$/.test(String(v)) ? String(v) : null;
  }

  function getOutputEl() {
    return document.querySelector(CFG.outputSelector) || document.getElementById(CFG.outputIdFallback) || null;
  }

  function getAttrEl() {
    return document.querySelector(CFG.attrSelector);
  }

  function parseMetafieldValue(value) {
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    if (!trimmed) return null;

    // Try JSON first, fallback to plain string
    try {
      return JSON.parse(trimmed);
    } catch (_) {
      return trimmed;
    }
  }

  /**
   * Shopify rich text JSON -> plain text.
   * Handles: root, paragraph, text (your current payload shape).
   */
  function richTextJsonToText(node) {
    if (!node) return '';

    // If we got a string that is JSON, parse it
    if (typeof node === 'string') {
      try { node = JSON.parse(node); } catch { return String(node); }
    }

    const walk = (n) => {
      if (!n) return '';
      if (n.type === 'text') return n.value || '';

      const childrenText = (n.children || []).map(walk).join('');

      // Paragraphs separated by blank line (tweak if you want single newline)
      if (n.type === 'paragraph') return childrenText + '\n\n';

      return childrenText;
    };

    return walk(node).trim();
  }

  function render(parsedValue, rawValue) {
    const out = getOutputEl();
    const holder = getAttrEl();

    // Optional attribute write (keeps raw JSON accessible)
    if (holder) {
      if (parsedValue == null) {
        holder.removeAttribute(CFG.attrName);
      } else {
        holder.setAttribute(
          CFG.attrName,
          typeof parsedValue === 'string' ? parsedValue : JSON.stringify(parsedValue)
        );
      }
    }

    if (!out) return;

    if (parsedValue == null) {
      out.textContent = '';
      return;
    }

    // ✅ FINAL DROP-IN: render parsed JSON to TEXT (instead of JSON.stringify)
    if (typeof parsedValue === 'object') {
      out.textContent = richTextJsonToText(parsedValue);
      return;
    }

    // string/number/bool
    out.textContent = String(parsedValue ?? rawValue ?? '');
  }

  async function fetchVariantMetafield(variantId) {
    if (state.cache.has(variantId)) return state.cache.get(variantId);

    const endpoint = `https://${Shopify.shop}/api/${CFG.apiVersion}/graphql.json`;

    const query = `
      query VariantMetafield($id: ID!) {
        node(id: $id) {
          ... on ProductVariant {
            specificVariantData: metafield(
              namespace: "${CFG.metafield.namespace}",
              key: "${CFG.metafield.key}"
            ) { value }
          }
        }
      }
    `;

    const body = JSON.stringify({
      query,
      variables: { id: `gid://shopify/ProductVariant/${variantId}` }
    });

    const res = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': CFG.storefrontToken
      },
      body
    });

    const json = await res.json();

    if (json.errors?.length) {
      console.error('[specificVariantData] GraphQL errors:', json.errors);
    }

    const raw = json?.data?.node?.specificVariantData?.value ?? null;
    const parsed = parseMetafieldValue(raw);

    const payload = { raw, parsed };
    state.cache.set(variantId, payload);
    return payload;
  }

  async function fetchAndRender(variantId) {
    try {
      const { raw, parsed } = await fetchVariantMetafield(variantId);
      render(parsed, raw);
    } catch (err) {
      console.error('[specificVariantData] fetch failed:', err);
      render(null, null);
    }
  }

  function observeVariantInput() {
    const input = document.querySelector('.product-form form input[name="id"]');
    if (!input) return;

    const mo = new MutationObserver(() => {
      const id = getCurrentVariantId();
      if (!id || id === state.lastVariantId) return;
      state.lastVariantId = id;
      fetchAndRender(id);
    });

    mo.observe(input, { attributes: true, attributeFilter: ['value'] });

    input.addEventListener('change', () => {
      const id = getCurrentVariantId();
      if (!id || id === state.lastVariantId) return;
      state.lastVariantId = id;
      fetchAndRender(id);
    });
  }
})();
